import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "../services/api";
import { todayLocalISO } from "../utils/date";
import styles from "./ProfileModal.module.css";
import ModalHeader from "./ModalHeader";
import SplitDateInput from "./SplitDateInput";
import PlusBadge from "./PlusBadge";
import { applyProfessorTheme, themeColors, pickTextFamilyForBg, previewAccentHex } from "../utils/theme";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faKey, faCheck, faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { useBodyScrollLock } from "../hooks/useBodyScrollLock";
import PasswordRequirementsChecklist, { isPasswordStrong } from "./PasswordRequirementsChecklist";

const ALL_COLOR_PRESETS = [
  "#1d9e75", "#10b981", "#059669", "#84cc16", "#14b8a6", "#22d3ee",
  "#06b6d4", "#0ea5e9", "#38bdf8", "#2563eb", "#6366f1", "#8b5cf6",
  "#a855f7", "#d946ef", "#f43f5e", "#fb923c", "#ef4444", "#f97316",
  "#f59e0b", "#d97706", "#eab308", "#6b7280",
];

const GENDER_OPTIONS = [
  { value: "masculino",            label: "Masculino" },
  { value: "feminino",             label: "Feminino" },
  { value: "outro",                label: "Outro" },
];

const GOAL_OPTIONS = [1, 2, 3, 4, 5, 6, 7];

export default function ProfileModal({ open, onClose, user, onUpdated, isPlus }) {
  const [name, setName]           = useState(user?.name || "");
  const [gender, setGender]       = useState(user?.gender || "");
  const [birthDate, setBirthDate] = useState(user?.birth_date || "");
  const [file, setFile]           = useState(null);
  const [brandColor, setBrandColor] = useState(user?.own_brand_color || "");
  const [bgColorDark, setBgColorDark] = useState(user?.own_bg_color_dark || "");
  const [bgColorLight, setBgColorLight] = useState(user?.own_bg_color_light || "");
  const [weeklyGoal, setWeeklyGoal] = useState(user?.weekly_goal ?? 3);
  useBodyScrollLock();
  const [saving, setSaving]       = useState(false);

  // Password section
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword]         = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPwd, setShowPwd]                 = useState(false);
  const [pwdSaving, setPwdSaving]             = useState(false);
  const [pwdError, setPwdError]               = useState("");
  const [pwdSuccess, setPwdSuccess]           = useState("");

  const isSelfService = !!user?.is_self_service;
  // Nome mantido por compatibilidade (ainda só cobria Google), mas vale
  // pra qualquer conta OAuth-only sem senha própria — Apple incluída.
  const isGoogleOnly  = user?.auth_provider === "google" || user?.auth_provider === "apple";
  const hasPassword   = user?.auth_provider === "hybrid" || user?.auth_provider === "email" || user?.auth_provider === "local";

  // Cor pessoal: só quando o professor NÃO define uma cor de marca
  const canPickColor = !user?.professor_brand_color
    && user?.role !== "professor" && user?.role !== "admin";
  const inputFileRef = useRef(null);

  useEffect(() => {
    if (open) {
      setName(user?.name || "");
      setGender(user?.gender || "");
      setBirthDate(user?.birth_date || "");
      setBrandColor(user?.own_brand_color || "");
      setBgColorDark(user?.own_bg_color_dark || "");
      setBgColorLight(user?.own_bg_color_light || "");
      setWeeklyGoal(user?.weekly_goal ?? 3);
      setFile(null);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setPwdError("");
      setPwdSuccess("");
    }
  }, [open, user]);

  const apiUrl = process.env.REACT_APP_API_URL;

  const previewUrl = useMemo(() => {
    if (file) return URL.createObjectURL(file);
    const avatarUrl = user?.avatar_url;
    const fallbackName = user?.name || "User";
    if (!avatarUrl) return `https://ui-avatars.com/api/?name=${encodeURIComponent(fallbackName)}`;
    let value = String(avatarUrl).trim();
    try { value = decodeURIComponent(value); } catch { /* use original */ }
    value = value.replace(/^\/+/, "");
    if (/^https?:\/\//i.test(value)) return value;
    const normalizedBase = (apiUrl || "").replace(/\/+$/, "");
    const normalizedPath = value.replace(/^\/+/, "");
    return `${normalizedBase}/${normalizedPath}`;
  }, [file, user, apiUrl]);

  useEffect(() => {
    return () => {
      if (file) URL.revokeObjectURL(previewUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file]);

  if (!open) return null;

  const save = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("name", name);
      if (gender) fd.append("gender", gender);
      if (birthDate) fd.append("birth_date", birthDate);
      if (canPickColor) {
        fd.append("brand_color", brandColor || "__clear__");
        fd.append("brand_bg_color_dark", bgColorDark || "__clear__");
        fd.append("brand_bg_color_light", bgColorLight || "__clear__");
      }
      if (isSelfService) fd.append("weekly_goal", String(weeklyGoal));
      if (file) fd.append("photo", file);

      const res = await api.put("/users/me/profile", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      applyProfessorTheme(res.data?.professor_brand_color ?? res.data?.own_brand_color ?? null, undefined, {
        dark: res.data?.professor_bg_color_dark ?? res.data?.own_bg_color_dark ?? null,
        light: res.data?.professor_bg_color_light ?? res.data?.own_bg_color_light ?? null,
      });
      onUpdated?.(res.data);
      onClose?.();
    } finally {
      setSaving(false);
    }
  };

  const savePassword = async () => {
    setPwdError("");
    setPwdSuccess("");
    if (hasPassword && !currentPassword) {
      setPwdError("Informe sua senha atual.");
      return;
    }
    if (!isPasswordStrong(newPassword)) {
      setPwdError("A senha precisa ter pelo menos 8 caracteres, 1 maiúscula, 1 número e 1 caractere especial.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setPwdError("As senhas não conferem.");
      return;
    }
    setPwdSaving(true);
    try {
      const res = await api.put("/users/me/password", {
        ...(hasPassword ? { current_password: currentPassword } : {}),
        new_password: newPassword,
        confirm_password: confirmPassword,
      });
      setPwdSuccess(res.data?.detail || "Senha definida com sucesso!");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      // update auth_provider so the label changes
      onUpdated?.({ ...user, auth_provider: "hybrid" });
    } catch (err) {
      setPwdError(err?.response?.data?.detail || "Erro ao definir senha.");
    } finally {
      setPwdSaving(false);
    }
  };

  const chipStyle = (active) => ({
    padding: "6px 13px", borderRadius: 20, fontSize: 13,
    border: active ? "1px solid rgba(var(--accent-rgb),0.6)" : "1px solid var(--border-0-10)",
    background: active ? "var(--accent-0-15)" : "var(--border-0-04)",
    color: active ? themeColors.blue : "var(--text-0-70)",
    fontWeight: active ? 600 : 400,
    cursor: "pointer", transition: "all .15s",
  });

  return (
    <div className={styles.backdrop} onClick={onClose} role="presentation">
      <div className={styles.modal} onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Meu perfil">
        <ModalHeader onClose={onClose} />
        <div className={styles.header}>
          <div>
            <div className={styles.title}>Meu perfil</div>
            <div className={styles.sub}>Atualize seu nome e avatar</div>
          </div>
        </div>

        <div className={styles.body}>
          {/* ── Avatar ── */}
          <div className={styles.avatarRow}>
            <span className={styles.avatarWrap}>
              <img src={previewUrl} className={styles.avatar} alt="Avatar" loading="lazy" referrerPolicy="no-referrer" />
              {isPlus && <PlusBadge size={22} title="Você é assinante Plus" />}
            </span>
            <div className={styles.avatarActions}>
              <button type="button" className={styles.btn} onClick={() => inputFileRef.current?.click()}>
                Trocar foto
              </button>
              <input
                ref={inputFileRef} type="file" accept="image/*"
                style={{ display: "none" }}
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
              <div className={styles.hint}>PNG/JPG • vai virar quadrado e leve</div>
            </div>
          </div>

          {/* ── Nome ── */}
          <div className={styles.field}>
            <label className={styles.label}>Nome</label>
            <input className={styles.input} value={name} onChange={(e) => setName(e.target.value)} placeholder="Seu nome" />
          </div>

          {/* ── Gênero ── */}
          <div className={styles.field}>
            <label className={styles.label}>Gênero</label>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {GENDER_OPTIONS.map(opt => (
                <button key={opt.value} type="button" onClick={() => setGender(opt.value)} style={chipStyle(gender === opt.value)}>
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* ── Data de nascimento ── */}
          <div className={styles.field}>
            <label className={styles.label}>Data de nascimento</label>
            <SplitDateInput
              value={birthDate}
              onChange={setBirthDate}
              inputClassName={styles.input}
              maxDate={todayLocalISO()}
            />
          </div>

          {/* ── Cor do app ── */}
          {canPickColor && (
            <div className={styles.field}>
              <label className={styles.label}>Cor do app</label>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
                {ALL_COLOR_PRESETS.map((hex) => (
                  <button key={hex} type="button" title={hex} onClick={() => setBrandColor(hex)} style={{
                    width: 30, height: 30, borderRadius: "50%", background: hex,
                    border: brandColor === hex ? "2px solid #fff" : "2px solid transparent",
                    outline: brandColor === hex ? `2px solid ${hex}` : "none", outlineOffset: 2,
                    cursor: "pointer", transition: "transform .15s",
                    transform: brandColor === hex ? "scale(1.15)" : "scale(1)",
                  }} />
                ))}
                <label title="Cor personalizada" style={{
                  width: 30, height: 30, borderRadius: "50%",
                  background: "conic-gradient(#f43f5e, #eab308, var(--feedback-success), #06b6d4, #6366f1, #d946ef, #f43f5e)",
                  border: brandColor && !ALL_COLOR_PRESETS.includes(brandColor) ? "2px solid #fff" : "2px solid transparent",
                  position: "relative", overflow: "hidden", cursor: "pointer", flexShrink: 0, display: "inline-block",
                }}>
                  <input type="color"
                    value={/^#[0-9A-Fa-f]{6}$/.test(brandColor || "") ? brandColor : "#1d9e75"}
                    onChange={(e) => setBrandColor(e.target.value)}
                    style={{ position: "absolute", inset: 0, opacity: 0, width: "100%", height: "100%", cursor: "pointer" }}
                  />
                </label>
                {brandColor && (
                  <button type="button" title="Voltar ao padrão" onClick={() => setBrandColor("")} style={{
                    width: 30, height: 30, borderRadius: "50%",
                    background: "rgba(255,255,255,0.06)", border: "2px dashed rgba(255,255,255,0.25)",
                    color: "var(--text-0-50)", fontSize: 15, lineHeight: 1, cursor: "pointer",
                  }}>×</button>
                )}
              </div>
              {brandColor && /^#[0-9A-Fa-f]{6}$/.test(brandColor) && (
                <div style={{ display: "flex", alignItems: "center", gap: 14, marginTop: 8 }}>
                  <span style={{ fontSize: 11, color: "var(--text-0-50)" }}>Prévia:</span>
                  {[
                    { key: "dark", label: "escuro", bg: bgColorDark },
                    { key: "light", label: "claro", bg: bgColorLight },
                  ].map(({ key, label, bg }) => {
                    const previewHex = previewAccentHex(brandColor, key, bg) || brandColor;
                    const adjusted = previewHex.toLowerCase() !== brandColor.toLowerCase();
                    return (
                      <span key={key} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11, color: "var(--text-0-60)" }}>
                        <span
                          title={adjusted ? `Ajustada automaticamente pra manter contraste legível no modo ${label}` : `Como vai aparecer no modo ${label}`}
                          style={{
                            width: 20, height: 20, borderRadius: "50%", background: previewHex,
                            border: "1px solid var(--border-strong)", display: "inline-block",
                          }}
                        />
                        {label}{adjusted && <span style={{ color: "var(--text-0-50)" }}>*</span>}
                      </span>
                    );
                  })}
                </div>
              )}
              <small className={styles.hint}>
                Personaliza os destaques do app só para você. A cor se ajusta sozinha pra continuar legível em
                cada modo (a prévia acima mostra o tom final que será salvo) - salve para aplicar.
              </small>
            </div>
          )}

          {/* ── Fundo personalizado (opcional) ── */}
          {canPickColor && (
            <div className={styles.field}>
              <label className={styles.label}>Fundo personalizado (opcional)</label>
              <small className={styles.hint} style={{ display: "block", marginBottom: 10 }}>
                Substitui o fundo padrão do app por um valor exato, um pra cada modo do seu
                alternador escuro/claro (que continua funcionando - só a cor de base de cada
                lado muda). O texto se ajusta sozinho pra continuar legível em qualquer cor.
                Deixe em branco pra manter o padrão nesse modo.
              </small>

              {[
                { key: "dark", label: "Fundo no modo escuro", value: bgColorDark, setValue: setBgColorDark },
                { key: "light", label: "Fundo no modo claro", value: bgColorLight, setValue: setBgColorLight },
              ].map(({ key, label, value, setValue }) => (
                <div key={key} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                  <label
                    title="Escolher cor"
                    style={{
                      width: 30, height: 30, borderRadius: 8,
                      background: /^#[0-9A-Fa-f]{6}$/.test(value || "") ? value : "var(--surface-tertiary)",
                      border: "2px solid var(--border-strong)",
                      cursor: "pointer", position: "relative", overflow: "hidden", flexShrink: 0,
                    }}
                  >
                    <input
                      type="color"
                      value={/^#[0-9A-Fa-f]{6}$/.test(value || "") ? value : "#000000"}
                      onChange={(e) => setValue(e.target.value)}
                      style={{ position: "absolute", inset: 0, opacity: 0, width: "100%", height: "100%", cursor: "pointer" }}
                    />
                  </label>
                  <span style={{ fontSize: 13, color: "var(--text-0-60)", minWidth: 130 }}>{label}</span>
                  <span style={{ fontSize: 12, color: "var(--text-0-50)", fontFamily: "monospace" }}>
                    {value || "padrão do app"}
                  </span>
                  {/^#[0-9A-Fa-f]{6}$/.test(value || "") && (
                    <span
                      title={`Prévia - texto ${pickTextFamilyForBg(value).family === "dark" ? "claro" : "escuro"} aplicado automaticamente pra manter o contraste`}
                      style={{
                        display: "inline-flex", alignItems: "center", justifyContent: "center",
                        width: 26, height: 26, borderRadius: 6, fontSize: 11, fontWeight: 700,
                        background: value,
                        color: pickTextFamilyForBg(value).textHex,
                        border: "1px solid var(--border-strong)", flexShrink: 0,
                      }}
                    >Aa</span>
                  )}
                  {value && (
                    <button
                      type="button"
                      title="Remover"
                      aria-label={`Remover cor de fundo do ${label.toLowerCase()}`}
                      onClick={() => setValue("")}
                      style={{
                        width: 26, height: 26, borderRadius: "50%",
                        background: "rgba(255,255,255,0.06)",
                        border: "2px dashed rgba(255,255,255,0.25)",
                        color: "var(--text-0-50)", fontSize: 14, lineHeight: 1,
                        cursor: "pointer", marginLeft: "auto",
                      }}
                    >×</button>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* ── Meta semanal ── */}
          <div className={styles.field}>
            <label className={styles.label}>Meta semanal</label>
            {isSelfService ? (
              <>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {GOAL_OPTIONS.map(n => (
                    <button key={n} type="button" onClick={() => setWeeklyGoal(n)} style={chipStyle(weeklyGoal === n)}>
                      {n}×
                    </button>
                  ))}
                </div>
                <small className={styles.hint}>Treinos por semana - usado na sua meta de cadência.</small>
              </>
            ) : (
              <>
                <div className={styles.readonlyBox}>
                  {typeof user?.weekly_goal === "number" && user.weekly_goal >= 0
                    ? `${user.weekly_goal} treinos/sem` : "-"}
                </div>
                <small className={styles.hint}>Sua meta é definida pelo seu professor ou administrador.</small>
              </>
            )}
          </div>

          {/* ── Senha ── */}
          {(
            <div className={styles.passwordSection}>
              <div className={styles.passwordSectionHeader}>
                <FontAwesomeIcon icon={faKey} />
                {hasPassword ? "Alterar senha" : "Definir senha"}
              </div>
              <p className={styles.passwordSectionHint}>
                {isGoogleOnly
                  ? `Crie uma senha para também entrar com email e senha, além ${user?.auth_provider === "apple" ? "da Apple" : "do Google"}.`
                  : "Altere sua senha de acesso por email."}
              </p>

              {hasPassword && (
                <div className={styles.field} style={{ marginTop: 4 }}>
                  <label className={styles.label}>Senha atual</label>
                  <div className={styles.pwdWrap}>
                    <input
                      type={showPwd ? "text" : "password"}
                      className={styles.input}
                      value={currentPassword}
                      onChange={e => { setCurrentPassword(e.target.value); setPwdError(""); setPwdSuccess(""); }}
                      placeholder="Sua senha atual"
                      autoComplete="current-password"
                    />
                  </div>
                </div>
              )}

              <div className={styles.field} style={{ marginTop: 4 }}>
                <label className={styles.label}>Nova senha</label>
                <div className={styles.pwdWrap}>
                  <input
                    type={showPwd ? "text" : "password"}
                    className={styles.input}
                    value={newPassword}
                    onChange={e => { setNewPassword(e.target.value); setPwdError(""); setPwdSuccess(""); }}
                    placeholder="Mínimo 8 caracteres"
                    autoComplete="new-password"
                  />
                  <button type="button" className={styles.pwdToggle} onClick={() => setShowPwd(v => !v)} tabIndex={-1}>
                    <FontAwesomeIcon icon={showPwd ? faEyeSlash : faEye} />
                  </button>
                </div>
                {newPassword && <PasswordRequirementsChecklist password={newPassword} />}
              </div>

              <div className={styles.field}>
                <label className={styles.label}>Confirmar senha</label>
                <div className={styles.pwdWrap}>
                  <input
                    type={showPwd ? "text" : "password"}
                    className={styles.input}
                    value={confirmPassword}
                    onChange={e => { setConfirmPassword(e.target.value); setPwdError(""); setPwdSuccess(""); }}
                    placeholder="Repita a senha"
                    autoComplete="new-password"
                  />
                </div>
              </div>

              {pwdError   && <p className={styles.pwdError}>{pwdError}</p>}
              {pwdSuccess && <p className={styles.pwdSuccess}><FontAwesomeIcon icon={faCheck} /> {pwdSuccess}</p>}

              <button
                type="button"
                className={styles.btnPwdSave}
                onClick={savePassword}
                disabled={pwdSaving || !isPasswordStrong(newPassword) || !confirmPassword || (hasPassword && !currentPassword)}
              >
                {pwdSaving ? "Salvando..." : (hasPassword ? "Alterar senha" : "Definir senha")}
              </button>
            </div>
          )}
        </div>


        <div className={styles.footer}>

          <button className={styles.btnGhost} type="button" onClick={onClose} disabled={saving}>
            Cancelar
          </button>
          <button className={styles.btnPrimary} type="button" onClick={save} disabled={saving}>
            {saving ? "Salvando..." : "Salvar"}
          </button>
        </div>
      </div>
    </div>
  );
}
